<template>
  <div class="company-logo-wrap">
    <LoadingSpinner v-if="uploading" :display-text="loadingSpinnerText" />
    <transition name="quickfade" appear mode="out-in">

      <!-- STAGE: GENERATOR -->
      <div v-if="stage === 'generator'" class="company-logo" key="step-generator">

        <div style="display: flex; justify-content: space-between; align-items: center;">
          <GekkoButton 
            @click="goToStage('menu')"
            :icon="'fa-solid fa-arrow-left'"
            :text="$gettext('Back')"
            variant="link text-blue"
            class="p-0 mb-3"
          />
          <div v-if="!currentCompany.is_premium && logoBotLoaded">
            <GekkoButton 
              id="tt-ai-generator"
              @click="upgradeToPremium"
              :text="aiCreditsLeft + ' | ' + $gettext('Upgrade')"
              icon="fa-regular fa-sparkles"
              variant="outline-brand"
              class="ai-credits-counter p-0 mb-3"
            />
            <b-tooltip target="tt-ai-generator" placement="top" triggers="hover">
              <div class="tooltip-text">
                {{ $gettext('As a basic user, you only have limited credits to use the AI tools on Gekko. Please consider upgrading to Premium if you run out.') }}
              </div>
            </b-tooltip>
          </div>
          
        </div>

        <div class="company-logo--sectionbox wizard-modal-onboarding-form">
          <ImageGenerator @generated="onImageGenerated" @loaded="onLogoBotLoaded" />
        </div>
      </div>

      <!-- STAGE: EDITOR -->
      <ImageEditor
        v-if="stage === 'editor'"
        :image-src="unsavedB64image || selectedImageSrc"
        @save-edit="onEditedImage"
        :generated-image="isImageGenerated"
        @go-back="goToStage('menu')"
        key="step-editor"
      />

      <!-- STAGE: GALLERY -->
      <div v-if="stage === 'gallery'" class="company-logo-gallery-wrap" key="step-gallery" style="display: flex; flex-direction: column;">
        <div>
          <GekkoButton 
            @click="goToStage('menu')"
            :icon="'fa-solid fa-arrow-left'"
            :text="$gettext('Back')"
            variant="link text-blue"
            class="p-0 mb-3"
          />
        </div>
        <overlay-scrollbars :options="{ scrollbars: { autoHide: 'never' }, overflowBehavior: { y: 'scroll', x: 'hidden' } }">
          <ImageGallery 
            :click-action="'select'"
            @edit-img="editFromGallery"
            @selected-img="onGalleryImageSelected"
          />
        </overlay-scrollbars>
      </div>


      <!-- STAGE: MENU -->
      <div v-if="stage === 'menu'" key="step-menu" class="step-confirm-wrap">
        <!-- image dropzone -->
        <ImageDropzone
          :ref="'image-dropzone'"
          :key="dropzoneKey"
          :image_field_name="'logo'"
          :options="{ 
            autoProcessQueue: false, 
            url: Urls['company_profile_detail'](currentCompany.id),
            delete_url: Urls['company_profile_delete_logo'](currentCompany.id),
            clickable: ['#open-file-selector']
          }"
          @sending="uploading = true"
          @uploaded="onImageUploaded"
          @change="onImageUploaded"
          @removed="removeLogo"
          hide-preview hide-zone
        />
        <!-- options menu -->
        <div class="gk-image-uploader--actions" style="margin-bottom: 1rem;">
          <GekkoButton
            @click="stage = 'main'"
            :icon="'fa-solid fa-arrow-left'"
            :text="$gettext('Back')"
            variant="link text-blue"
            class="p-0 mb-3"
          />
          <ul>
            <li v-if="selectedImageSrc">
              <GekkoButton :text="$gettext('Crop & Resize')" @click="stage = 'editor'" size="lg" variant="light btn-playful--icon-blue" :icon="'fa-solid fa-pen-ruler'" />
            </li>
            <li>
              <GekkoButton :text="$gettext('Generate logo with AI')" @click="stage = 'generator'" size="lg" variant="light btn-playful--icon-blue" :icon="'fa-solid fa-sparkles'" />
            </li>
            <li>
              <GekkoButton :text="$gettext('Upload new logo')" size="lg" variant="light btn-playful--icon-blue" key="open-file-selector" id="open-file-selector" :icon="'fa-solid fa-camera'" />
            </li>
            <li>
              <GekkoButton :text="$gettext('Select from gallery')" size="lg" variant="light btn-playful--icon-blue" @click="stage = 'gallery'" :icon="'fa-solid fa-images'" />
            </li>
            <li>
              <GekkoButton :text="$gettext('Remove')" size="lg" variant="light btn-playful--icon-blue" v-if="selectedImageSrc" @click="removeLogo" :icon="'fa-solid fa-xmark'" />
              <GekkoButton :text="$gettext('Don\'t need a logo')" size="lg" variant="light btn-playful--icon-blue" v-else @click="dontNeedLogo" :icon="'fa-solid fa-xmark'" />
            </li>
          </ul>
        </div>
      </div>

      <!-- STAGE: MAIN -->
      <div v-if="stage === 'main'" key="step-confirm" class="step-confirm-wrap">
        <!-- SELECTED IMAGE VIEW -->
        <div class="company-logo-confirm" key="company-logo-confirm" ref="component-container" :style="{ '--wiz-height-from': '198px', '--wiz-height-to': '60px' }">
          <div class="selected-item-confirm" style="margin-bottom: 1rem;">
            <div v-if="unsavedB64image || selectedImageSrc" class="selected-item-confirm--logo">
              <img :src="unsavedB64image || selectedImageSrc" :alt="companyOrBrand.name">
            </div>
            <div v-else class="selected-item-confirm--logo no-logo">
              {{ $gettext('No logo') }}
            </div>
            <div v-if="showName" class="selected-item-confirm--name">{{ companyOrBrand.name }}</div>
            <!-- options menu (dropdown) -->
            <div class="edit-dropdown">
              <GekkoButton 
                @click="stage = 'menu'"
                :text="$gettext('Edit')"
                variant="link"
                icon="fa-solid fa-pencil"
                class="text-blue p-0 edit-btn"
                size="sm"
              />
            </div>
          </div>
          <div v-if="wizard">
            <GekkoButton
              v-if="selectedImageSrc"
              @click="completeCompanyLogo"
              :text="$gettext('That\'s my logo')"
              icon="fa-solid fa-check"
              variant="brand"
              size="lg"
              class="w-100 justify-content-center"
            />
            <GekkoButton
              v-else
              @click="$emit('step-skipped', false)"
              :text="$gettext('Don\'t need a logo')"
              :icon="'fa-solid fa-xmark'"
              variant="orange"
              size="lg"
              class="w-100 justify-content-center"
            />
          </div>
          
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'

import GekkoButton from "@/components/GekkoButton";
import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"

import ImageEditor from '@/components/logo_generator/ImageEditor'
import ImageGallery from '@/components/logo_generator/ImageGallery'
import ImageGenerator from '@/components/logo_generator/ImageGenerator'
import ImageDropzone from '@/components/logo_generator/ImageDropzone'

import AiAssistantService from '@/store/services/ai.assistant.service.js'

export default {
  name: 'CompanyLogo',
  components: {
    ImageEditor,
    ImageGallery,
    ImageGenerator,
    ImageDropzone,
    GekkoButton,
    LoadingSpinner
  },
  props: {
    wizard: {
      type: Boolean,
      default: true
    },
    showName: {
      type: Boolean,
      default: true
    },
    type: {
      type: String,
      default: 'company'
    }
  },
  data () {
    return {
      stage: 'main', // main, menu, generator, editor, gallery
      uploading: false,
      dropzoneKey: 0,
      loadingSpinnerText: '',
      unsavedB64image: null,
      stabilityMaxPixelSize: 4000000,
      logoBotLimit: null,
      logoBotLoaded: false
    }
  },
  computed: {
    ...mapGetters({
      currentCompany: "main/getCurrentCompany",
      currentUser: "main/getCurrentUser",
      selectedBrand: "brands/getSelectedBrand"
    }),
    companyOrBrand () {
      let companyOrBrand
      if (this.type === 'company') {
        companyOrBrand = this.currentCompany
      }
      if (this.type === 'brand') {
        companyOrBrand = this.selectedBrand
      }
      return companyOrBrand
    },
    selectedImageSrc () {
      let logo = null
      if (this.type === 'company' && this.currentCompany.logo) {
        logo = this.currentCompany.logo
      }
      if (this.type === 'brand' && this.selectedBrand.brand_logo) {
        logo = this.selectedBrand.brand_logo
      }
      return logo
    },
    isImageGenerated () {
      return (this.unsavedB64image && true)
    },
    aiCreditsLeft () {
      return Math.max(0, this.logoBotLimit - this.currentUser.profile.ai_image_counter);
    }
  },
  created () {
    this.setupData()
  },
  methods: {
    ...mapMutations({ 
      incrementAiCounter: 'main/SET_INCREMENT_CURRENT_USER_AI_COUNTER'
    }),
    ...mapActions({ 
      uploadCompanyLogo: 'main/uploadCompanyLogo',
      deleteCompanyLogo: 'main/deleteCompanyLogo',
      uploadBrandLogo: 'brands/uploadBrandLogo',
      deleteBrandLogo: 'brands/deleteBrandLogo'
    }),
    setupData () {
      this.loadingSpinnerText = this.$gettext('Saving image')
      if (this.selectedImageSrc) {
        this.goToStage('main')
      } else {
        this.goToStage('menu')
      }
    },
    onLogoBotLoaded (payload) {
      if (this.currentCompany.is_premium) {
        this.logoBotLimit = payload.premium
      } else {
        this.logoBotLimit = payload.basic
      }
      this.logoBotLoaded = true
    },
    editFromGallery (image_src) {
      // TODO option currently disabled
      this.stage = 'editor'
    },
    onImageGenerated (payload) {
      this.incrementAiCounter()
      window.mixpanel.track("Wizard: logo - generated")
      this.unsavedB64image = payload
      this.stage = 'editor'
    },
    onGalleryImageSelected (payload) {
      window.mixpanel.track("Wizard: logo - gallery selected")
      this.saveToCompany(payload.id, 'main')
    },
    onEditedImage (data) {
      window.mixpanel.track("Wizard: logo - edited")
      this.saveLogo(this.base64ToFile(data, 'logo_image.png', 'image/png'), 'main', true)
    },
    onImageUploaded (image) {
      window.mixpanel.track("Wizard: logo - uploaded")
      this.resizeImage(image).then((imageBlob) => {
        image = new File([imageBlob], 'logo_image.png', { type: imageBlob.type })
        this.saveLogo(image, 'main', true)
      })
    },
    async saveToCompany (image_id, next = 'main') {
      AiAssistantService.getLogoImageB64(image_id).then((response) => {
        this.saveLogo(this.base64ToFile(response.data, 'logo_image.png', 'image/png'), next, false)
      }).catch((error) => {
        console.log(error)
      })

    },
    /**
     * saveLogo will upload to company or brand and logos (if save_to_logos is true)
     * save_to_logos will be false when selecting from gallery
     */
    saveLogo (new_logo = null, next = 'editor', save_to_logos = true) {
      this.loadingSpinnerText = this.$gettext('Saving image')
      this.uploading = true
      if (this.type === 'company') {
        this.saveCompanyLogo(new_logo, next, save_to_logos)
      }
      if (this.type === 'brand') {
        this.saveBrandLogo(new_logo, next, save_to_logos)
      }
    },
    saveCompanyLogo (new_logo, save_to_logos, next) {
      // save company then to gallery if necessary
      const company_data = JSON.parse(JSON.stringify(this.currentCompany))
      company_data.logo = new_logo
      this.uploadCompanyLogo(company_data).then(() => {
        this.saveToLogoGallery(new_logo, save_to_logos, next)
      })
    },
    saveBrandLogo (new_logo, save_to_logos, next) {
      if (this.selectedBrand.id) {
        // existing brand, save brand then to gallery if necessary
        const brand_data = JSON.parse(JSON.stringify(this.selectedBrand))
        brand_data.brand_logo = new_logo
        this.uploadBrandLogo(brand_data).then(() => {
          this.saveToLogoGallery(new_logo, next, save_to_logos)
        })
      } else {
        // new brand, just emit and save to gallery if necessary
        this.fileToBase64(new_logo)
        this.$emit('attach-logo', new_logo)
        this.saveToLogoGallery(new_logo, next, save_to_logos, true)
      }
    },
    saveToLogoGallery (new_logo, next, save_to_logos, keep_unsaved = false) {
      if (save_to_logos) {
        // save to logo gallery
        const new_logo_data = {
          name: 'logo_image.png',
          image: new_logo,
          user: this.currentUser.id
        }
        AiAssistantService.uploadNewLogo(new_logo_data).then((response) => {
          this.goToStage(next, keep_unsaved)
          this.uploading = false
        }).catch((error) => {
          console.log(error)
        })
      } else {
        this.goToStage(next, keep_unsaved)
        this.uploading = false
      }
    },
    dontNeedLogo () {
      if (this.type === 'brand' && !this.selectedBrand.id) {
        this.$emit('detach-logo', false)
      }
      this.goToStage('main')
    },
    removeLogo () {
      window.mixpanel.track("Wizard: logo - removed")
      this.loadingSpinnerText = this.$gettext('Removing logo')
      this.uploading = true
      if (this.type === 'company') {
        this.deleteCompanyLogo(this.currentCompany.id).then(() => {
          this.uploading = false
          this.goToStage('main')
        })
      }
      if (this.type === 'brand') {
        this.deleteBrandLogo(this.selectedBrand.id).then(() => {
        this.$emit('detach-logo', false)
          this.uploading = false
          this.goToStage('main')
        })
      }
    },
    goToStage (stage, keep_unsaved = false) {
      if (stage === 'main' && !keep_unsaved) {
        this.unsavedB64image = null
      }
      this.dropzoneKey++
      this.stage = stage
    },
    base64ToFile (base64String, fileName, mimeType) {
      const binaryString = atob(base64String.split(',')[1])
      const len = binaryString.length
      const bytes = new Uint8Array(len)
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i)
      }
      const blob = new Blob([bytes], { type: mimeType })
      return new File([blob], fileName, { type: mimeType })
    },
    async urlToFile (image_url) {
      const image_data = await fetch(image_url, { mode: 'no-cors' })
      const blob = await image_data.blob();
      const file = new File([blob], 'logo_image.png', { type: blob.type });
      return file
    },
    fileToBase64 (file) {
      const reader = new FileReader()
      reader.onload = (event) => {
        this.unsavedB64image = event.target.result
      }
      reader.readAsDataURL(file)
    },
    resizeImage(binaryData) {
      const blob = new Blob([binaryData], { type: 'image/jpeg' })

      const img = new Image()

      const promise = new Promise((resolve, reject) => {
        img.onload = () => {
          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')
          
          const scaleFactor = Math.sqrt(this.stabilityMaxPixelSize / (img.width * img.height))
          const newWidth = img.width * scaleFactor
          const newHeight = img.height * scaleFactor

          canvas.width = newWidth
          canvas.height = newHeight
          
          ctx.drawImage(img, 0, 0, newWidth, newHeight)
          
          canvas.toBlob((blob) => {
            resolve(blob)
          }, 'image/jpeg')
        }

        img.onerror = reject
      })

      img.src = URL.createObjectURL(blob)

      return promise
    },
    /**
     * Final next step button
     */
    completeCompanyLogo () {
      this.$emit('step-completed', false)
    },
    upgradeToPremium () {
      window.location.href = '/subscriptions/manage'
    }
  }
}
</script>
<style lang="scss">
  .gk-image-uploader {
    display: flex;
    #dropzone-root .dropzone-preview-image img.dropzone-img {
      object-fit: cover;
    }
    &--uploader {
      width: 100%;
      max-width: 225px;
      aspect-ratio: 1/1;
      border-radius: $radiusLg;
      border: 1px solid #F4F4F4;
      overflow: hidden;
    }
    &--actions {
      width: 100%;
      ul {
        list-style: none;
        margin: 0;
        padding: 0;
        li {
          margin-bottom: 0.5rem;
          .btn {
            width: 100%;
            max-width: 300px;
            padding-left: 2rem;
            text-align: left;
            .svg-inline--fa {
              font-size: 20px;
              width: 24px;
              margin-right: 0.75rem;
            }
          }
        }
      }
    }
  }

.wizard-dropdown.gk-animated-dropdown {
  .dropdown-menu {
    width: auto !important;
    padding: 0.5rem !important;
    box-shadow: rgba(11, 20, 26, 0.26) 0px 2px 5px 0px, rgba(11, 20, 26, 0.16) 0px 2px 10px 0px !important;
    .dropdown-item {
      border-radius: calc(#{$gridBorderRadius} / 2);
      font-size: 16px;
      cursor: pointer;
      line-height: 1.5;
      padding: 0.5rem;
      .svg-inline--fa {
        margin-right: 0.5rem;
      }
      &:hover {
        background: rgba(34, 172, 138, 0.07);
        color: #06926f;
      }
    }
  }
}
</style>
<style lang="scss" scoped>
  .company-logo-wrap {
    width: 100%;
    .company-logo {
      display: flex;
      flex-direction: column;
      height: 100%;
      &--section, &--sectionbox {
        p { 
          margin-bottom: 0.5rem;
          font-size: 1rem;
          color: $colorTextSemi;
        }
      }

      .ai-credits-counter {
        padding: 0 0.5rem !important;
        border-width: 1px !important;
      }
      
    }
    .step-confirm-wrap {
      display: flex;
      justify-content: center;
    }
    .company-logo-confirm {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      max-width: 425px;
      width: 100%;
      .selected-item-confirm {
        cursor: initial;
        &--name {
          text-align: center;
          padding-top: 1.5rem;
          width: 100%;
        }
      }
    }
    .company-logo-gallery-wrap  {
      height: 100%;
    }
  }
</style>